import { stateful } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';

const cardClass =
  'flex-row bg-base-100 shadow-lg text-lg px-3 py-2 flex gap-2 items-center w-72 border-1 border-base-300 shadow-md rounded-box z-1 select-none';

const deactivatedClass = 'opacity-50 cursor-not-allowed';

const activeClass =
  'hover:brightness-110 hover:scale-102 active:scale-95 transition duration-150 ease-in-out cursor-pointer active:rotate-1 ';

const joinedCardClass = `join-item flex-1 flex-row bg-base-100 shadow-lg text-lg px-3 py-2 flex gap-2 items-center min-w-0 border-1 border-base-300 shadow-md z-1 select-none`;

const joinedCardStyle = (widthPercent: number) => `flex: 0 0 ${widthPercent}%; max-width: ${widthPercent}%;`;

interface ListOperationsMenuItem {
  label?: string;
  iconifyClass: string;
  additionalClasses: string;
  joinedWidthPercent?: number;
  active?: boolean;
  onClick: () => void;
}

interface ListOperationsMenuOptionsSection {
  title: string;
  subtext?: string;
  items: Array<ListOperationsMenuItem | Array<ListOperationsMenuItem>>;
}

export interface ListOperationsMenuOptions {
  /**
   * The button text for the dropdown
   * Defaults to: "Optionen"
   */
  title?: string;
  sections: ListOperationsMenuOptionsSection[];
}

const ListOperationsMenuItem = stateful(
  (props: { item: ListOperationsMenuItem | Array<ListOperationsMenuItem>; joined?: boolean }) => {
    const { item, joined = false } = props;

    /**
     * We can also handle sub arrays. These will be rendered as a joined row
     */
    if (Array.isArray(item) && item.length > 1) {
      return html`
        <div class="join w-72">
          ${repeat(
            item,
            item => item.label ?? item.iconifyClass,
            item => ListOperationsMenuItem({ item, joined: true }),
          )}
        </div>
      `;
    }

    const itemSingleEnsured = Array.isArray(item) ? item[0] : item;
    const joinedStyle =
      joined && itemSingleEnsured?.joinedWidthPercent ? joinedCardStyle(itemSingleEnsured.joinedWidthPercent) : nothing;

    return html`
      <div
        class="${joined ? joinedCardClass : cardClass} ${
          itemSingleEnsured?.active ? deactivatedClass : `${activeClass} ${itemSingleEnsured?.additionalClasses}`
        } ${itemSingleEnsured?.label ? 'justify-between' : 'justify-center'}"
        style=${joinedStyle}
        @click=${itemSingleEnsured?.onClick}
      >
        <span class="${itemSingleEnsured?.iconifyClass}"></span>
        ${
          itemSingleEnsured?.label
            ? html`<p class="truncate text-sm font-bold" title=${itemSingleEnsured.label}>
                ${itemSingleEnsured.label}
              </p>`
            : nothing
        }
      </div>
    `;
  },
);

const ListOperationsMenuSection = stateful((props: ListOperationsMenuOptionsSection) => {
  return html`
    <div class="flex items-center gap-2 justify-between min-w-max">
      <h3 class="font-semibold">${props.title}</h3>
      <div class="flex items-center gap-2"></div>
    </div>
    <!-- wraps at the item width: the panel is max-content wide, so an unwrapped hint would stretch it across the view -->
    ${props.subtext ? html`<p class="max-w-72 text-sm text-base-content/70">${props.subtext}</p>` : nothing}
    <ul class="flex flex-col gap-2">
      ${repeat(
        props.items,
        item => (Array.isArray(item) ? (item[0]?.label ?? item[0]?.iconifyClass) : (item.label ?? item.iconifyClass)),
        item => ListOperationsMenuItem({ item }),
      )}
    </ul>
  `;
});

export const ListOperationsMenu = stateful((props: ListOperationsMenuOptions) => {
  return html`
    <div class="dropdown dropdown-end">
      <div
        role="button"
        class="btn btn-primary dropdown-toggle"
        tabindex="0"
        aria-haspopup="true"
        aria-expanded="false"
        style="width: max-content"
      >
        <div class="flex items-center gap-1">
          <span class="icon-[material-symbols--arrow-drop-down-circle-outline]"></span>
          <p>Optionen</p>
        </div>
      </div>
      <!-- capped and scrolling on its own: a panel taller than the window would stretch the page beneath it -->
      <div
        tabindex="0"
        class="dropdown-content menu flex-col flex-nowrap card p-3 gap-3 max-w-none max-h-[70vh] overflow-y-auto overscroll-contain"
      >
        <div class="gap-3 flex flex-col">
          ${repeat(
            props.sections,
            section => section.title,
            section => ListOperationsMenuSection(section),
          )}
        </div>
      </div>
    </div>
  `;
});
